import { ADMIN_ROLES } from '../utils/roleHelpers';
/**
 * Application Routes Configuration
 * Includes all authentication flows and protected routes
 */

import React, { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { AuthProvider } from '../context/AuthContext';
import { EmailVerificationPage } from '../pages/auth/EmailVerificationPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { ForbiddenPage } from '../pages/errors/ForbiddenPage';
import { NotFoundPage } from '../pages/errors/NotFoundPage';

import { ProtectedRoute } from './ProtectedRoute';

// Lazy-load all feature pages so they are not included in the initial bundle
const DashboardPage = React.lazy(() =>
  import('../pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const TicketsPage = React.lazy(() =>
  import('../pages/tickets/TicketsPage').then((m) => ({ default: m.TicketsPage }))
);
const AttendancePage = React.lazy(() =>
  import('../pages/attendance/AttendancePage/AttendancePage').then((m) => ({
    default: m.AttendancePage,
  }))
);
const LeaveListPage = React.lazy(() =>
  import('../pages/attendance/LeaveListPage/LeaveListPage').then((m) => ({
    default: m.LeaveListPage,
  }))
);
const LeaveDetailsPage = React.lazy(() =>
  import('../pages/attendance/LeaveDetailsPage/LeaveDetailsPage').then((m) => ({
    default: m.LeaveDetailsPage,
  }))
);
const LeaveFormPage = React.lazy(() =>
  import('../pages/attendance/LeaveFormPage/LeaveFormPage').then((m) => ({
    default: m.LeaveFormPage,
  }))
);
const LeaveApprovalsPage = React.lazy(() =>
  import('../pages/attendance/LeaveApprovalsPage/LeaveApprovalsPage').then((m) => ({
    default: m.LeaveApprovalsPage,
  }))
);
const UserManagementPage = React.lazy(() =>
  import('../pages/users/UserManagementPage/UserManagementPage').then((m) => ({
    default: m.UserManagementPage,
  }))
);
const UserDetailsPage = React.lazy(() =>
  import('../pages/users/UserDetailsPage/UserDetailsPage').then((m) => ({
    default: m.UserDetailsPage,
  }))
);
const CreateEditUserPage = React.lazy(() =>
  import('../pages/users/CreateEditUserPage/CreateEditUserPage').then((m) => ({
    default: m.CreateEditUserPage,
  }))
);
const ProfilePage = React.lazy(() =>
  import('../pages/users/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const GroupManagementPage = React.lazy(() =>
  import('../pages/groups/GroupManagementPage').then((m) => ({ default: m.GroupManagementPage }))
);
const GroupDetailsPage = React.lazy(() =>
  import('../pages/groups/GroupDetailsPage').then((m) => ({ default: m.GroupDetailsPage }))
);
const CreateEditGroupPage = React.lazy(() =>
  import('../pages/groups/CreateEditGroupPage').then((m) => ({ default: m.CreateEditGroupPage }))
);
const AdminPayslipManagementPage = React.lazy(() =>
  import('../pages/payroll/AdminPayslipManagementPage').then((m) => ({
    default: m.AdminPayslipManagementPage,
  }))
);
const PayslipUploadPage = React.lazy(() =>
  import('../pages/payroll/PayslipUploadPage').then((m) => ({ default: m.PayslipUploadPage }))
);
const EmployeePayslipPage = React.lazy(() =>
  import('../pages/payroll/EmployeePayslipPage').then((m) => ({ default: m.EmployeePayslipPage }))
);
const PayslipDownloadPage = React.lazy(() =>
  import('../pages/payroll/PayslipDownloadPage').then((m) => ({ default: m.PayslipDownloadPage }))
);
const LeaveReportsPage = React.lazy(() =>
  import('../pages/reports/LeaveReportsPage').then((m) => ({ default: m.LeaveReportsPage }))
);
const ConsolidateLeaveReportsPage = React.lazy(() =>
  import('../pages/reports/consolidateLeaveReport').then((m) => ({
    default: m.ConsolidateLeaveReportsPage,
  }))
);
const PreOnboardingReportsPage = React.lazy(() =>
  import('../pages/preonboarding/PreOnboardingReportsPage').then((m) => ({
    default: m.PreOnboardingReportsPage,
  }))
);
const OnboardingReportsPage = React.lazy(() =>
  import('../pages/onboarding/OnboardingReportsPage/OnboardingReportsPage').then((m) => ({
    default: m.OnboardingReportsPage,
  }))
);
const PolicyManagementPage = React.lazy(() =>
  import('../pages/settings/PolicyManagementPage').then((m) => ({
    default: m.PolicyManagementPage,
  }))
);
const EmployeesPage = React.lazy(() =>
  import('../pages/management/EmployeesPage/EmployeesPage').then((m) => ({
    default: m.EmployeesPage,
  }))
);
const EmployeeDetailPage = React.lazy(() =>
  import('../pages/management/EmployeeDetailPage/EmployeeDetailPage').then((m) => ({
    default: m.EmployeeDetailPage,
  }))
);
const OnboardingFormPage = React.lazy(() =>
  import('../pages/onboarding/OnboardingFormPage/OnboardingFormPage').then((m) => ({
    default: m.OnboardingFormPage,
  }))
);
const PreOnboardingListPage = React.lazy(() =>
  import('../pages/preonboarding/PreOnboardingListPage').then((m) => ({
    default: m.PreOnboardingListPage,
  }))
);
const PreOnboardingViewPage = React.lazy(() =>
  import('../pages/preonboarding/PreOnboardingViewPage').then((m) => ({
    default: m.PreOnboardingViewPage,
  }))
);
const CreatePreOnboardingPage = React.lazy(() =>
  import('../pages/preonboarding/CreatePreOnboardingPage').then((m) => ({
    default: m.CreatePreOnboardingPage,
  }))
);
const CandidatePreOnboardingPage = React.lazy(() =>
  import('../pages/preonboarding/CandidatePreOnboardingPage').then((m) => ({
    default: m.CandidatePreOnboardingPage,
  }))
);
const OnboardingListPage = React.lazy(() =>
  import('../pages/onboarding/OnboardingListPage').then((m) => ({ default: m.OnboardingListPage }))
);
const FlatOnboardingViewPage = React.lazy(() =>
  import('../pages/onboarding/OnboardingViewPage').then((m) => ({ default: m.OnboardingViewPage }))
);
const TimesheetEntryPage = React.lazy(() =>
  import('../pages/timesheet/TimesheetPage/TimesheetEntryPage').then((m) => ({
    default: m.TimesheetEntryPage,
  }))
);
const TimesheetApprovalsPage = React.lazy(() =>
  import('../pages/timesheet/TimesheetApprovalsPage/TimesheetApprovalsPage').then((m) => ({
    default: m.TimesheetApprovalsPage,
  }))
);
const AdminTimesheetListPage = React.lazy(() =>
  import('../pages/timesheet/AdminTimesheetListPage/AdminTimesheetListPage').then((m) => ({
    default: m.AdminTimesheetListPage,
  }))
);

// v7_startTransition/v7_relativeSplatPath were v6 opt-in future flags for
// behavior that is simply the default in v7 — the `future` prop was removed
// from BrowserRouterProps entirely once that became true.
export const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
          <Routes>
            {/* Public authentication routes — kept as direct imports (critical path, small) */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/verify-email" element={<EmailVerificationPage />} />

            {/* Legacy login route redirect */}
            <Route path="/login" element={<Navigate to="/auth/login" replace />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* User Management Routes — admin/super_admin only */}
              <Route
                path="users"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <UserManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="users/create"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <CreateEditUserPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="users/new"
                element={<Navigate to="/users/create" replace />}
              />
              <Route
                path="users/:id"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <UserDetailsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="users/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <CreateEditUserPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Group Management Routes — admin/super_admin only */}
              <Route
                path="groups"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <GroupManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="groups/create"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <CreateEditGroupPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="groups/:id"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <GroupDetailsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="groups/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <CreateEditGroupPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Ticket Management Routes */}
              <Route
                path="tickets"
                element={
                  <ErrorBoundary>
                    <TicketsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="tickets/*"
                element={
                  <ErrorBoundary>
                    <TicketsPage />
                  </ErrorBoundary>
                }
              />

              <Route
                path="tickets/:id"
                element={
                  <ErrorBoundary>
                    <TicketsPage />
                  </ErrorBoundary>
                }
              />

              {/* Attendance & Leave Routes */}
              <Route
                path="attendance"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <AttendancePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance/leaves"
                element={
                  <ErrorBoundary>
                    <LeaveListPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="attendance/leaves/policies"
                element={<Navigate to="/settings/policy-management" replace />}
              />
              <Route
                path="attendance/leaves/:leaveId"
                element={
                  <ErrorBoundary>
                    <LeaveDetailsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="attendance/leaves/apply"
                element={
                  <ErrorBoundary>
                    <LeaveFormPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="attendance/leaves/approvals"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <LeaveApprovalsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Timesheet Routes */}
              <Route
                path="timesheets"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TimesheetEntryPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="timesheets/approvals"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TimesheetApprovalsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Payroll Routes */}
              <Route
                path="payroll/manage"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <AdminPayslipManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="payroll/upload"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <PayslipUploadPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="payroll/my-payslips"
                element={
                  <ErrorBoundary>
                    <EmployeePayslipPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="payroll/download"
                element={
                  <ErrorBoundary>
                    <PayslipDownloadPage />
                  </ErrorBoundary>
                }
              />

              {/* Settings Routes */}
              <Route
                path="settings/policy-management"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <PolicyManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Management Routes */}
              <Route
                path="management/employees"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <EmployeesPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="management/timesheets"
                element={
                  <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                    <ErrorBoundary>
                      <AdminTimesheetListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="management/employees/:id"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <EmployeeDetailPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Reports Routes — admin/super_admin only */}
              <Route
                path="reports/leaves"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <LeaveReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports/consolidate"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ConsolidateLeaveReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports/consolidate"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ConsolidateLeaveReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports/preonboarding"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <PreOnboardingReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports/onboarding"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <OnboardingReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Profile Route */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProfilePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Pre-Onboarding Routes */}
              <Route
                path="pre-onboarding"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <PreOnboardingListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="pre-onboarding/create"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <CreatePreOnboardingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="pre-onboarding/:id"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <PreOnboardingViewPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="pre-onboarding/:id/edit"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <CreatePreOnboardingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Onboarding Routes — also open to HR / Talent Acquisition employees */}
              <Route
                path="onboarding"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <OnboardingListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="onboarding/:id"
                element={
                  <ProtectedRoute
                    allowedRoles={ADMIN_ROLES}
                    allowedDepartments={['HR', 'Talent Acquisition']}
                  >
                    <ErrorBoundary>
                      <FlatOnboardingViewPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Legacy redirects */}
              <Route
                path="admin/leave-policies"
                element={<Navigate to="/settings/policy-management" replace />}
              />
              <Route path="departments" element={<Navigate to="/users?departments=true" replace />} />
              <Route path="attendance/timesheets" element={<Navigate to="/timesheets" replace />} />
              <Route path="leave" element={<Navigate to="/attendance/leaves" replace />} />
            </Route>

            {/* Public candidate-facing forms — no auth required */}
            <Route
              path="/onboarding/candidate/:token"
              element={
                <ErrorBoundary>
                  <CandidatePreOnboardingPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/pre-onboarding/candidate/:token"
              element={
                <ErrorBoundary>
                  <CandidatePreOnboardingPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/candidate-onboarding/:token"
              element={
                <ErrorBoundary>
                  <OnboardingFormPage />
                </ErrorBoundary>
              }
            />

            {/* Error pages */}
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};
